import { api, getApiUrl, getApiKey, getCachedWorkspace } from '../api-client'
import { getGitRemote, isGitRepo } from '../git'
import { existsSync, readFileSync } from 'node:fs'
import { resolve } from 'node:path'
import { resolveRepo, notLinkedMessage, formatDiagnostics } from '../repo-resolver'
import { analyticsEvent, flushAndExit } from '../analytics'

interface CheckResult {
  name: string
  status: 'ok' | 'warn' | 'fail'
  detail: string
  section?: 'core' | 'enrichment'
}

export async function doctorCommand(args: string[] = []): Promise<void> {
  const isJSON = args.includes('--json')
  const checks: CheckResult[] = []

  // 1. API key present (core)
  const apiKey = getApiKey()
  if (apiKey) {
    checks.push({ name: 'API key', status: 'ok', detail: 'present', section: 'core' })
  } else {
    checks.push({ name: 'API key', status: 'fail', detail: 'Not found. Run `driftless login --key <key>`', section: 'core' })
  }

  // 2. API reachable (core)
  const apiUrl = getApiUrl()
  try {
    await api.get('/health')
    checks.push({ name: 'API reachable', status: 'ok', detail: apiUrl, section: 'core' })
  } catch {
    checks.push({ name: 'API reachable', status: 'fail', detail: `Cannot reach ${apiUrl}`, section: 'core' })
  }

  // 3. Git remote (core)
  const remote = getGitRemote()
  if (remote) {
    checks.push({ name: 'Git remote', status: 'ok', detail: `${remote.org}/${remote.repo}`, section: 'core' })
  } else {
    checks.push({ name: 'Git remote', status: 'fail', detail: 'No remote.origin.url found', section: 'core' })
  }

  // Cache /me result for the GitHub App check
  let meCache: { workspace_id?: string; slug?: string } | null = null
  let observedWorkspaceSlug: string | undefined
  async function getMe(): Promise<{ workspace_id?: string; slug?: string } | null> {
    if (meCache) return meCache
    try {
      meCache = (await api.get('/me', { timeoutMs: 8000, retries: 1 })) as { workspace_id?: string; slug?: string }
      observedWorkspaceSlug = meCache?.slug
    } catch {
      meCache = null
    }
    return meCache
  }

  // 4. Workspace  +  5. Repo linked  +  6. Baseline — all derived from the
  // SAME resolveRepo() the commands use, so doctor reports the slug that
  // actually resolves instead of a separate /me call that can disagree.
  const resolution = await resolveRepo()
  const localOrNoKey = !apiKey || apiUrl === 'http://localhost:3000/api/v1'

  if (localOrNoKey) {
    checks.push({ name: 'Workspace', status: 'warn', detail: 'Skipped (local API or no key)', section: 'core' })
  } else if (resolution.ok) {
    checks.push({ name: 'Workspace', status: 'ok', detail: `${resolution.workspaceSlug} (source: ${resolution.source})`, section: 'core' })
  } else if (resolution.reason === 'not_linked' && resolution.workspaceSlug) {
    checks.push({ name: 'Workspace', status: 'ok', detail: resolution.workspaceSlug, section: 'core' })
  } else if (resolution.reason === 'no_workspace' && resolution.diagnostics) {
    const d = resolution.diagnostics
    const detail = !d.meReturned && d.meError
      ? `/me failed (${d.meError}); cached=${d.configSlug ?? 'none'}`
      : 'Could not resolve. Run `driftless doctor` or check your API key.'
    checks.push({ name: 'Workspace', status: 'warn', detail, section: 'core' })
  } else {
    checks.push({ name: 'Workspace', status: 'warn', detail: 'Could not resolve (no git remote)', section: 'core' })
  }

  if (resolution.ok) {
    checks.push({ name: 'Repo linked', status: 'ok', detail: `${resolution.remote.org}/${resolution.remote.repo}`, section: 'enrichment' })
  } else if (resolution.reason === 'not_linked') {
    checks.push({ name: 'Repo linked', status: 'warn', detail: notLinkedMessage(resolution.remote!, resolution.workspaceSlug!), section: 'enrichment' })
  } else if (resolution.reason === 'no_workspace') {
    checks.push({ name: 'Repo linked', status: 'warn', detail: 'Could not verify (workspace unknown)', section: 'enrichment' })
  } else {
    checks.push({ name: 'Repo linked', status: 'warn', detail: 'Skipped (no git remote)', section: 'enrichment' })
  }

  // 7. AGENTS.md (enrichment)
  const agentsPath = resolve(process.cwd(), 'AGENTS.md')
  if (existsSync(agentsPath)) {
    const content = readFileSync(agentsPath, 'utf-8')
    if (content.includes('driftless')) {
      checks.push({ name: 'AGENTS.md', status: 'ok', detail: 'Driftless skill installed', section: 'enrichment' })
    } else {
      checks.push({ name: 'AGENTS.md', status: 'warn', detail: 'File exists but no Driftless references. Run `driftless install-skill`', section: 'enrichment' })
    }
  } else {
    checks.push({ name: 'AGENTS.md', status: 'warn', detail: 'Not found. Run `driftless install-skill`', section: 'enrichment' })
  }

  // 8. GitHub App (enrichment)
  if (isGitRepo() && remote) {
    const me = await getMe()
    if (me?.slug) {
      try {
        const integrations = (await api.get(`/workspaces/${me.slug}/integrations`)) as Array<{ type: string; active: boolean }>
        const ghApp = integrations.find((i) => i.type === 'github')
        if (ghApp && ghApp.active) {
          checks.push({ name: 'GitHub App', status: 'ok', detail: 'Installed and active', section: 'enrichment' })
        } else if (ghApp && !ghApp.active) {
          checks.push({ name: 'GitHub App', status: 'warn', detail: 'Installed, activating — wait ~60s then re-run doctor', section: 'enrichment' })
        } else {
          checks.push({ name: 'GitHub App', status: 'warn', detail: 'Not installed — app.driftless.icu → Settings → Integrations', section: 'enrichment' })
        }
      } catch (err: any) {
        const msg = String(err?.message ?? '')
        const isNotFound = err?.status === 404 || /HTTP 404|404/.test(msg)
        if (isNotFound) {
          checks.push({ name: 'GitHub App', status: 'warn', detail: 'Not installed — app.driftless.icu → Settings → Integrations', section: 'enrichment' })
        } else if (/timed out|Connection failed|ENOTFOUND|ECONNREFUSED/i.test(msg)) {
          checks.push({ name: 'GitHub App', status: 'warn', detail: 'Could not verify — API unreachable (network/timeout)', section: 'enrichment' })
        } else {
          checks.push({ name: 'GitHub App', status: 'warn', detail: 'Could not verify — API key may lack permission for this workspace', section: 'enrichment' })
        }
      }
    } else {
      checks.push({ name: 'GitHub App', status: 'warn', detail: 'Could not verify (workspace unknown)', section: 'enrichment' })
    }
  } else {
    checks.push({ name: 'GitHub App', status: 'warn', detail: 'Skipped (not a git repo)', section: 'enrichment' })
  }

  // Machine-readable output for agents / CI parsing `doctor`.
  if (isJSON) {
    const ok = checks.filter((c) => c.status === 'ok').length
    const warn = checks.filter((c) => c.status === 'warn').length
    const fail = checks.filter((c) => c.status === 'fail').length
    const distinctId = resolution.ok ? resolution.workspaceSlug : observedWorkspaceSlug ?? getCachedWorkspace().slug
    if (distinctId) {
      analyticsEvent('cli_doctor', distinctId, {
        result: fail > 0 ? 'fail' : warn > 0 ? 'warn' : 'ok',
        failed_check_count: fail,
      })
    }
    const out: Record<string, unknown> = {
      ok: fail === 0,
      summary: { ok, warn, fail },
      checks: checks.map((c) => {
        const base = { name: c.name, status: c.status, detail: c.detail, section: c.section ?? 'core' }
        if (c.name === 'API key' && c.status === 'ok') {
          return { ...base, key_prefix: 'drift_****' }
        }
        return base
      }),
    }
    if (!resolution.ok && resolution.reason === 'no_workspace' && resolution.diagnostics) {
      out['workspace_diagnostics'] = resolution.diagnostics
    }
    console.log(JSON.stringify(out, null, 2))
    await flushAndExit(fail > 0 ? 1 : 0)
  }

  // Render — split into Core context and Repo enrichment sections
  const coreChecks = checks.filter((c) => (c.section ?? 'core') === 'core')
  const enrichmentChecks = checks.filter((c) => c.section === 'enrichment')

  const padWidth = Math.max(...checks.map((c) => c.name.length)) + 2
  let coreOk = 0, coreWarn = 0, coreFail = 0
  let enrichOk = 0, enrichWarn = 0

  console.log('Driftless Doctor — environment check\n')

  console.log('Core context')
  for (const c of coreChecks) {
    const icon = c.status === 'ok' ? '✓' : c.status === 'warn' ? '⚠' : '✗'
    const statusLabel = c.status === 'ok' ? 'OK' : c.status === 'warn' ? 'WARN' : 'FAIL'
    console.log(`  ${icon} ${pad(c.name, padWidth)} ${statusLabel}  ${c.detail}`)
    if (c.status === 'ok') coreOk++
    else if (c.status === 'warn') coreWarn++
    else coreFail++
  }

  console.log('\nRepo enrichment')
  for (const c of enrichmentChecks) {
    const icon = c.status === 'ok' ? '✓' : c.status === 'warn' ? '○' : '✗'
    const statusLabel = c.status === 'ok' ? 'OK' : c.status === 'warn' ? 'INFO' : 'FAIL'
    console.log(`  ${icon} ${pad(c.name, padWidth)} ${statusLabel}  ${c.detail}`)
    if (c.status === 'ok') enrichOk++
    else enrichWarn++
  }

  console.log(`\nCore: ${coreOk} ok, ${coreFail} failures  ·  Enrichment: ${enrichOk} ok, ${enrichWarn} available`)

  if (!resolution.ok && resolution.reason === 'no_workspace' && resolution.diagnostics) {
    console.log('\nWorkspace resolution diagnostics:')
    console.log(formatDiagnostics(resolution.diagnostics))
  }

  // Only core failures cause exit 1. Enrichment warnings are informational.
  const coreFailCount = coreChecks.filter((c) => c.status === 'fail').length
  if (coreFailCount > 0) {
    console.log('\nFix core failures before using Driftless. Enrichment is optional.')
    const distinctId = resolution.ok ? resolution.workspaceSlug : observedWorkspaceSlug ?? getCachedWorkspace().slug
    if (distinctId) {
      analyticsEvent('cli_doctor', distinctId, {
        result: 'fail',
        failed_check_count: coreFailCount,
      })
    }
    await flushAndExit(1)
  }
  const distinctId = resolution.ok ? resolution.workspaceSlug : observedWorkspaceSlug ?? getCachedWorkspace().slug
  if (distinctId) {
    analyticsEvent('cli_doctor', distinctId, {
      result: coreWarn > 0 || enrichWarn > 0 ? 'warn' : 'ok',
      failed_check_count: 0,
    })
  }
  await flushAndExit(0)
}

function pad(s: string, n: number): string {
  if (s.length >= n) return s
  return s + ' '.repeat(n - s.length)
}
